import React from 'react';
import { SofaIcon, BedDoubleIcon, LampFloorIcon, ShowerHeadIcon, RulerIcon, TableIcon, LampDeskIcon, PanelTopIcon, PlusCircleIcon, CheckCircleIcon, MinusCircleIcon } from 'lucide-react';
interface ItemSelectorProps {
  category: string;
  selectedItem: string;
  onItemSelect: (item: string) => void;
  onAddToCart: (item: {
    id: string;
    name: string;
    price: number;
    category: string;
  }) => void;
  onRemoveFromCart?: (item: {
    id: string;
    name: string;
    price: number;
    category: string;
  }) => void;
  cartItems?: Array<{
    id: string;
    name: string;
    price: number;
    category: string;
  }>;
}
export const ItemSelector = ({
  category,
  selectedItem,
  onItemSelect,
  onAddToCart,
  onRemoveFromCart,
  cartItems = []
}: ItemSelectorProps) => {
  // Check if item is already in cart
  const isItemInCart = (itemId: string, category: string) => {
    return cartItems.some(cartItem => cartItem.id === itemId && cartItem.category === category);
  };
  // Get items based on category
  const getItemsByCategory = (categoryId: string) => {
    const items: {
      [key: string]: {
        id: string;
        name: string;
        price: number;
        icon: JSX.Element;
      }[];
    } = {
      ukseliistud: [{
        id: 'item1',
        name: 'Milano 3-kohaline',
        price: 899,
        icon: <SofaIcon size={24} data-id="element-32" />
      }, {
        id: 'item2',
        name: 'Oslo nurgadiivan',
        price: 1299,
        icon: <SofaIcon size={24} data-id="element-33" />
      }, {
        id: 'item3',
        name: 'Stockholm tugitool',
        price: 499,
        icon: <SofaIcon size={24} data-id="element-34" />
      }],
      põrandaliistud: [{
        id: 'item1',
        name: 'Premium voodi 160x200',
        price: 799,
        icon: <BedDoubleIcon size={24} data-id="element-35" />
      }, {
        id: 'item2',
        name: 'Deluxe voodi 180x200',
        price: 999,
        icon: <BedDoubleIcon size={24} data-id="element-36" />
      }],
      valisnurgad: [{
        id: 'item1',
        name: 'Köögikomplekt Basic',
        price: 1299,
        icon: <TableIcon size={24} data-id="element-37" />
      }, {
        id: 'item2',
        name: 'Köögikomplekt Premium',
        price: 1899,
        icon: <TableIcon size={24} data-id="element-38" />
      }],
      lävepakud: [{
        id: 'item1',
        name: 'Modern rippvalgusti',
        price: 199,
        icon: <LampFloorIcon size={24} data-id="element-39" />
      }, {
        id: 'item2',
        name: 'Classic põrandalamp',
        price: 159,
        icon: <LampFloorIcon size={24} data-id="element-40" />
      }],
      sisenurgad: [{
        id: 'item1',
        name: 'Premium dušinurk',
        price: 699,
        icon: <ShowerHeadIcon size={24} data-id="element-41" />
      }, {
        id: 'item2',
        name: 'Deluxe vannitoakomplekt',
        price: 1299,
        icon: <ShowerHeadIcon size={24} data-id="element-42" />
      }],
      nelikantulistud: [{
        id: 'item1',
        name: 'Valge põrandaliist 12cm',
        price: 12,
        icon: <RulerIcon size={24} data-id="element-43" />
      }, {
        id: 'item2',
        name: 'Tamm põrandaliist 10cm',
        price: 15,
        icon: <RulerIcon size={24} data-id="element-44" />
      }],
      seinapaneelid: [{
        id: 'item1',
        name: 'Round söögilaud 120cm',
        price: 399,
        icon: <TableIcon size={24} data-id="element-45" />
      }, {
        id: 'item2',
        name: 'Nordic söögilaud 160cm',
        price: 599,
        icon: <TableIcon size={24} data-id="element-46" />
      }],
      klotsid: [{
        id: 'item1',
        name: 'LED laelamp 40cm',
        price: 129,
        icon: <LampDeskIcon size={24} data-id="element-47" />
      }, {
        id: 'item2',
        name: 'Smart öölamp',
        price: 79,
        icon: <LampDeskIcon size={24} data-id="element-48" />
      }],
      dekoorid: [{
        id: 'item1',
        name: '3D seinapaneel valge',
        price: 45,
        icon: <PanelTopIcon size={24} data-id="element-49" />
      }, {
        id: 'item2',
        name: 'Puitpaneel tamm',
        price: 55,
        icon: <PanelTopIcon size={24} data-id="element-50" />
      }]
    };
    return items[categoryId] || [];
  };
  const categoryItems = getItemsByCategory(category);
  return <div className="py-2 px-4" data-id="element-51">
      <div className="flex overflow-x-auto gap-4" data-id="element-52">
        {categoryItems.map((item: {
        id: string;
        name: string;
        price: number;
        icon: JSX.Element;
      }) => {
        const itemInCart = isItemInCart(item.id, category);
        return <div key={item.id} className={`flex flex-col items-center py-2 px-3 border-2 rounded-md cursor-pointer transition-all min-w-[160px] ${itemInCart ? 'border-green-500 bg-green-50' : selectedItem === item.id ? 'border-orange-500 bg-orange-50' : 'border-transparent hover:border-gray-200'}`} onClick={() => onItemSelect(item.id)} data-id="element-53">
                <div className="w-24 h-24 bg-gray-100 rounded-md overflow-hidden flex items-center justify-center mb-1 relative" data-id="element-54">
                  {item.icon}
                  {itemInCart && <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full p-0.5" data-id="element-55">
                      <CheckCircleIcon size={16} data-id="element-56" />
                    </div>}
                </div>
                <div className="w-full" data-id="element-57">
                  <div className="flex justify-between items-center mt-1" data-id="element-58">
                    <div className="font-medium text-sm truncate max-w-[90px]" data-id="element-59">
                      {item.name}
                    </div>
                    <div className="text-orange-500 font-semibold text-sm" data-id="element-60">
                      {item.price} €
                    </div>
                  </div>
                  <button className={`w-full px-2 py-0.5 rounded-md transition-colors flex items-center justify-center gap-1 text-xs mt-1 ${itemInCart ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`} onClick={e => {
              e.stopPropagation();
              if (itemInCart) {
                if (onRemoveFromCart) {
                  onRemoveFromCart({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    category: category
                  });
                }
              } else {
                onAddToCart({
                  id: item.id,
                  name: item.name,
                  price: item.price,
                  category: category
                });
              }
            }} data-id="element-61">
                    {itemInCart ? <>
                        <MinusCircleIcon size={12} data-id="element-62" />
                        <span data-id="element-63">Eemalda</span>
                      </> : <>
                        <PlusCircleIcon size={12} data-id="element-64" />
                        <span data-id="element-65">Lisa pakkumisse</span>
                      </>}
                  </button>
                </div>
              </div>;
      })}
      </div>
    </div>;
};